import { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Calendar, Clock, CreditCard, Loader2, Send } from "lucide-react";
import { api } from "@/lib/api";

export const Booking = forwardRef((props, ref) => {
  const [services, setServices] = useState([]);
  const [service, setService] = useState("");
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState([]);
  const [slot, setSlot] = useState("");
  const [form, setForm] = useState({ client_name: "", client_email: "", client_phone: "", message: "" });
  const [allSlots, setAllSlots] = useState(["09:00", "12:00", "15:00", "18:00"]);
  const [loading, setLoading] = useState(false);

  useImperativeHandle(ref, () => ({ preselect: (slug) => setService(slug) }));

  useEffect(() => {
    api.get("/services").then(({ data }) => setServices(data));
    api.get("/slots").then(({ data }) => setAllSlots(data.slots));
  }, []);
  useEffect(() => {
    if (!date) return;
    setSlot("");
    api.get(`/availability?date=${date}`).then(({ data }) => setSlots(data.slots));
  }, [date]);

  const today = new Date().toISOString().split("T")[0];
  const valid = service && date && slot && form.client_name && form.client_email;

  const createBooking = async () => {
    const { data } = await api.post("/bookings", { service_slug: service, date, slot, ...form });
    return data;
  };

  const payDeposit = async () => {
    if (!valid) { toast.error("Complétez la prestation, la date, le créneau et vos coordonnées."); return; }
    setLoading(true);
    try {
      const booking = await createBooking();
      const { data } = await api.post("/payments/checkout", { booking_id: booking.id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Une erreur est survenue.");
      setLoading(false);
    }
  };

  const sendRequest = async () => {
    if (!valid) { toast.error("Complétez la prestation, la date, le créneau et vos coordonnées."); return; }
    setLoading(true);
    try {
      await createBooking();
      toast.success("Demande envoyée ! Le studio vous recontacte sous 24h.");
      setService(""); setDate(""); setSlot(""); setForm({ client_name: "", client_email: "", client_phone: "", message: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Une erreur est survenue.");
    }
    setLoading(false);
  };

  const inputCls = "w-full bg-[#0D0D0C] border border-[#F4F0EB]/10 rounded-lg px-4 py-3 font-sub text-sm text-[#F4F0EB] placeholder:text-[#6E695E] focus:border-[#C88D51]/60 focus:outline-none transition-colors";

  return (
    <section id="reservation" className="py-24 lg:py-32 bg-[#141412]">
      <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
        <div className="overline mb-4">Réservation en ligne</div>
        <h2 className="font-serif-d text-4xl lg:text-6xl tracking-tight mb-4">Réservez votre shooting.</h2>
        <p className="font-sub text-[#A39E93] mb-14 max-w-xl">Choisissez une prestation, un créneau, puis confirmez avec un acompte de 150€ en ligne — ou envoyez simplement votre demande.</p>

        <div className="grid lg:grid-cols-2 gap-10">
          {/* step 1 & 2 */}
          <div className="space-y-8">
            <div>
              <div className="font-mono-a text-xs uppercase tracking-[0.2em] text-[#C88D51] mb-3">01 — Prestation</div>
              <select data-testid="booking-service-select" value={service} onChange={(e) => setService(e.target.value)} className={inputCls}>
                <option value="">Sélectionnez une prestation…</option>
                {services.map((s) => <option key={s.id} value={s.slug}>{s.name} — dès {s.price_from}€</option>)}
              </select>
            </div>

            <div>
              <div className="font-mono-a text-xs uppercase tracking-[0.2em] text-[#C88D51] mb-3">02 — Date & Créneau</div>
              <div className="relative mb-4">
                <Calendar className="w-4 h-4 text-[#C88D51] absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input data-testid="booking-date-picker" type="date" min={today} value={date} onChange={(e) => setDate(e.target.value)} className={inputCls + " pl-11"} />
              </div>
              <div className="grid grid-cols-4 gap-2">
                {(date ? slots : allSlots.map((t) => ({ time: t, available: true }))).map((s) => (
                  <button key={s.time} data-testid="booking-slot-button" disabled={date && !s.available}
                    onClick={() => setSlot(s.time)}
                    className={`py-3 rounded-lg font-mono-a text-sm border transition-colors flex items-center justify-center gap-1
                      ${slot === s.time ? "bg-[#C88D51] text-[#0D0D0C] border-[#C88D51]" : date && !s.available ? "border-[#F4F0EB]/5 text-[#6E695E] line-through cursor-not-allowed" : "border-[#F4F0EB]/10 text-[#F4F0EB] hover:border-[#C88D51]/50"}`}>
                    <Clock className="w-3 h-3" /> {s.time}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* step 3 */}
          <div className="bg-[#161513] border border-[#F4F0EB]/8 p-8 rounded-xl">
            <div className="font-mono-a text-xs uppercase tracking-[0.2em] text-[#C88D51] mb-5">03 — Vos coordonnées</div>
            <div className="space-y-3">
              <input data-testid="booking-name-input" placeholder="Nom complet" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} className={inputCls} />
              <input data-testid="booking-email-input" type="email" placeholder="Email" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} className={inputCls} />
              <input placeholder="Téléphone (optionnel)" value={form.client_phone} onChange={(e) => setForm({ ...form, client_phone: e.target.value })} className={inputCls} />
              <textarea placeholder="Votre projet en quelques mots…" rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={inputCls} />
            </div>

            <div className="flex items-center justify-between mt-6 pt-5 border-t border-[#F4F0EB]/8">
              <span className="font-sub text-sm text-[#A39E93]">Acompte de réservation</span>
              <span className="font-serif-d text-2xl text-[#C88D51]">150,00 €</span>
            </div>

            <button data-testid="booking-deposit-submit" onClick={payDeposit} disabled={loading} className="btn-accent w-full mt-4 py-3.5 font-sub text-sm flex items-center justify-center gap-2 disabled:opacity-60">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />} Confirmer & payer l'acompte
            </button>
            <button data-testid="booking-request-submit" onClick={sendRequest} disabled={loading} className="btn-ghost w-full mt-3 py-3 font-sub text-sm flex items-center justify-center gap-2 disabled:opacity-60">
              <Send className="w-4 h-4" /> Envoyer une demande sans acompte
            </button>
          </div>
        </div>
      </div>
    </section>
  );
});
